import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminShell from '../../components/layout/AdminShell';
import { getAdminNavigation } from '../../constants/navigation';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../lib/supabase';

interface LoanOfficerFormData {
  first_name: string;
  last_name: string;
  email: string;
  personal_email: string;
  phone: string;
  cell_number: string;
  nmls: string;
  dre_number: string;
  title: string;
  office_address: string;
  timezone: string;
  user_role: string;
  profile_flags: Record<string, boolean>;
}

const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
];

const USER_ROLES = [
  { value: 'loan_officer', label: 'Loan Officer' },
  { value: 'admin', label: 'Admin' },
  { value: 'recruiter', label: 'Recruiter' },
];

const PROFILE_FLAGS = [
  { key: 'has_ca_dre', label: 'Has CA DRE License', description: 'Show DRE-related tasks' },
  { key: 'works_in_office', label: 'Works in Office', description: 'Show parking pass and office-related tasks' },
  { key: 'has_mlo_license', label: 'Has MLO License', description: 'Show MLO-specific tasks' },
  { key: 'needs_laptop', label: 'Needs Company Laptop', description: 'Show laptop setup tasks' },
  { key: 'remote_worker', label: 'Remote Worker', description: 'Show remote work setup tasks' },
];

const LoanOfficerForm: React.FC = () => {
  const navigate = useNavigate();
  const { isVA } = useAuth();
  const navigation = getAdminNavigation(isVA);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<LoanOfficerFormData>({
    first_name: '',
    last_name: '',
    email: '',
    personal_email: '',
    phone: '',
    cell_number: '',
    nmls: '',
    dre_number: '',
    title: '',
    office_address: '',
    timezone: 'America/Los_Angeles',
    user_role: 'loan_officer',
    profile_flags: {},
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError(null);
  };

  const handleFlagChange = (flagKey: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      profile_flags: {
        ...prev.profile_flags,
        [flagKey]: checked,
      },
    }));
  };

  const validateForm = () => {
    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      setError('First and last name are required');
      return false;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const newLO = await api.createLoanOfficer({
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim().toLowerCase(),
        personal_email: formData.personal_email.trim() || null,
        phone: formData.phone.trim() || null,
        cell_number: formData.cell_number.trim() || null,
        nmls: formData.nmls.trim() || null,
        dre_number: formData.dre_number.trim() || null,
        title: formData.title.trim() || null,
        office_address: formData.office_address.trim() || null,
        timezone: formData.timezone,
        user_role: formData.user_role,
        profile_flags: formData.profile_flags,
      });

      // Initialize onboarding for the new LO
      await api.initializeLOOnboarding(newLO.id);

      // Navigate to the new LO's detail page
      navigate(`/admin/loan-officers/${newLO.id}`);
    } catch (err: any) {
      // Error handled
      setError(err.message || 'Failed to create loan officer. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminShell
      title="Add Loan Officer"
      description="Create a new loan officer profile and initialize their onboarding"
      nav={navigation}
      actions={null}
    >
      <button
        onClick={() => navigate('/admin/loan-officers')}
        className="text-sm text-slate-500 hover:text-slate-700"
      >
        ← Back to Loan Officers
      </button>

      <form onSubmit={handleSubmit} className="mt-6 bg-white border border-slate-200 rounded-3xl p-6 shadow-sm space-y-6">
        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Basic Information */}
        <div>
          <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-[0.2em] mb-4">
            Basic Information
          </h3>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <label className="text-sm font-semibold text-slate-700">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                className="mt-1 w-full rounded-2xl border border-slate-200 px-3 py-2 focus:border-lumin-primary focus:outline-none focus:ring-1 focus:ring-lumin-primary/40"
                required
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                className="mt-1 w-full rounded-2xl border border-slate-200 px-3 py-2 focus:border-lumin-primary focus:outline-none focus:ring-1 focus:ring-lumin-primary/40"
                required
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">
                Title
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="e.g., Senior Loan Officer"
                className="mt-1 w-full rounded-2xl border border-slate-200 px-3 py-2 focus:border-lumin-primary focus:outline-none focus:ring-1 focus:ring-lumin-primary/40"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">
                Role <span className="text-red-500">*</span>
              </label>
              <select
                name="user_role"
                value={formData.user_role}
                onChange={handleInputChange}
                className="mt-1 w-full rounded-2xl border border-slate-200 px-3 py-2 focus:border-lumin-primary focus:outline-none focus:ring-1 focus:ring-lumin-primary/40"
              >
                {USER_ROLES.map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div>
          <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-[0.2em] mb-4">
            Contact Information
          </h3>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <label className="text-sm font-semibold text-slate-700">
                Company Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="email@luminlending.com"
                className="mt-1 w-full rounded-2xl border border-slate-200 px-3 py-2 focus:border-lumin-primary focus:outline-none focus:ring-1 focus:ring-lumin-primary/40"
                required
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">
                Personal Email
              </label>
              <input
                type="email"
                name="personal_email"
                value={formData.personal_email}
                onChange={handleInputChange}
                placeholder="personal@gmail.com"
                className="mt-1 w-full rounded-2xl border border-slate-200 px-3 py-2 focus:border-lumin-primary focus:outline-none focus:ring-1 focus:ring-lumin-primary/40"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">
                Office Phone
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="(555) 123-4567"
                className="mt-1 w-full rounded-2xl border border-slate-200 px-3 py-2 focus:border-lumin-primary focus:outline-none focus:ring-1 focus:ring-lumin-primary/40"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">
                Cell Phone
              </label>
              <input
                type="tel"
                name="cell_number"
                value={formData.cell_number}
                onChange={handleInputChange}
                placeholder="(555) 987-6543"
                className="mt-1 w-full rounded-2xl border border-slate-200 px-3 py-2 focus:border-lumin-primary focus:outline-none focus:ring-1 focus:ring-lumin-primary/40"
              />
            </div>
          </div>
        </div>

        {/* License & Location */}
        <div>
          <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-[0.2em] mb-4">
            License & Location
          </h3>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <label className="text-sm font-semibold text-slate-700">
                NMLS Number
              </label>
              <input
                type="text"
                name="nmls"
                value={formData.nmls}
                onChange={handleInputChange}
                placeholder="123456"
                className="mt-1 w-full rounded-2xl border border-slate-200 px-3 py-2 focus:border-lumin-primary focus:outline-none focus:ring-1 focus:ring-lumin-primary/40"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">
                DRE Number
              </label>
              <input
                type="text"
                name="dre_number"
                value={formData.dre_number}
                onChange={handleInputChange}
                placeholder="01234567"
                className="mt-1 w-full rounded-2xl border border-slate-200 px-3 py-2 focus:border-lumin-primary focus:outline-none focus:ring-1 focus:ring-lumin-primary/40"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-semibold text-slate-700">
                Office Address
              </label>
              <textarea
                name="office_address"
                value={formData.office_address}
                onChange={handleInputChange}
                placeholder="123 Main St, Suite 100, City, State 12345"
                rows={2}
                className="mt-1 w-full rounded-2xl border border-slate-200 px-3 py-2 focus:border-lumin-primary focus:outline-none focus:ring-1 focus:ring-lumin-primary/40"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">
                Timezone
              </label>
              <select
                name="timezone"
                value={formData.timezone}
                onChange={handleInputChange}
                className="mt-1 w-full rounded-2xl border border-slate-200 px-3 py-2 focus:border-lumin-primary focus:outline-none focus:ring-1 focus:ring-lumin-primary/40"
              >
                {TIMEZONES.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz.replace('America/', '').replace('Pacific/', '').replace('_', ' ')}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Profile Flags */}
        <div>
          <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-[0.2em] mb-4">
            Task Visibility Flags
          </h3>
          <p className="text-sm text-slate-500 mb-4">
            Select which conditions apply to this loan officer. This determines which optional tasks they'll see.
          </p>
          <div className="space-y-3">
            {PROFILE_FLAGS.map((flag) => (
              <label key={flag.key} className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={formData.profile_flags[flag.key] || false}
                  onChange={(e) => handleFlagChange(flag.key, e.target.checked)}
                  className="mt-0.5 h-5 w-5 rounded border-slate-300 text-lumin-primary focus:ring-lumin-primary/40"
                />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-slate-900 group-hover:text-lumin-primary transition">
                    {flag.label}
                  </div>
                  <div className="text-xs text-slate-500">{flag.description}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="flex gap-3 justify-end pt-4 border-t border-slate-100">
          <button
            type="button"
            onClick={() => navigate('/admin/loan-officers')}
            className="px-5 py-2.5 rounded-2xl border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-5 py-2.5 rounded-2xl bg-lumin-primary text-white hover:bg-blue-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Creating…' : 'Create Loan Officer'}
          </button>
        </div>
      </form>
    </AdminShell>
  );
};

export default LoanOfficerForm;
